import type { LegalDoc } from '@/data/legal/types';

export const websiteTerms: LegalDoc = {
  title: 'Website Terms of Use',
  lastUpdated: '15 October 2026',
  sections: [
    {
      heading: '1. About This Website',
      blocks: [
        {
          p: 'This website is operated by Daniella Farrell, trading as KOR38, an independent business based in London, United Kingdom.',
        },
        {
          p: 'KOR38 provides personalised event-supplier sourcing and introduction services, primarily across Greater London, with enquiries from other locations considered.',
        },
        {
          p: 'These Website Terms of Use explain the conditions that apply when you access or use this website.',
        },
        { p: 'Contact: hello@kor38.com' },
        {
          p: 'By using this website, you agree to comply with these terms. If you do not agree, please discontinue using the website.',
        },
      ],
    },
    {
      heading: '2. Purpose of the Website',
      blocks: [
        {
          p: 'This website provides information about KOR38, its services, supplier network and opportunities to make enquiries or apply to work with the business.',
        },
        {
          p: 'KOR38 is an event-supplier sourcing and introduction service. It is not an event planning company, event management company, booking platform or public supplier directory.',
        },
        {
          p: 'Information on this website is provided for general information and to help visitors understand the services offered.',
        },
        {
          p: 'Submitting an enquiry or application does not guarantee that KOR38 will accept a request, provide a service or make a supplier introduction.',
        },
      ],
    },
    {
      heading: '3. Using This Website',
      blocks: [
        { p: 'You agree to use this website lawfully and responsibly.' },
        { p: 'You must not:' },
        {
          list: [
            'Use the website for fraudulent, misleading or unlawful purposes.',
            'Submit false, abusive or deliberately misleading information.',
            'Attempt to gain unauthorised access to the website, its systems or stored information.',
            'Introduce viruses, malicious code or other harmful material.',
            'Interfere with the website\u2019s operation or security.',
            'Copy, scrape or systematically extract website content or information for unauthorised commercial purposes.',
            'Use the website to infringe the rights of KOR38 or any third party.',
          ],
        },
        { p: 'We may take reasonable steps to protect the website and its users against misuse.' },
      ],
    },
    {
      heading: '4. Website Information and Accuracy',
      blocks: [
        { p: 'We aim to keep the information on this website accurate, clear and up to date.' },
        { p: 'However, website content may occasionally contain errors or become out of date.' },
        { p: 'Information about services, prices, availability and other arrangements may change.' },
        {
          p: 'Where you request a paid service, the specific scope, price and delivery arrangements will be confirmed before you enter into a contract.',
        },
        {
          p: 'Nothing on this website guarantees the availability, suitability or performance of any particular event supplier.',
        },
      ],
    },
    {
      heading: '5. Enquiries and Online Forms',
      blocks: [
        { p: 'The website may contain forms for:' },
        {
          list: [
            'Customer enquiries and service requests.',
            'Supplier applications.',
            'The Supplier Insights Survey.',
            'Requests for free resources, including the Event Supplier Checklist.',
            'General contact.',
          ],
        },
        {
          p: 'When submitting information, please ensure that it is accurate and that you are authorised to provide it.',
        },
        { p: 'Submitting a form does not automatically create a contractual relationship with KOR38.' },
        { p: 'Where a paid service is agreed, the separate Customer Terms & Conditions will apply.' },
        { p: 'Information submitted through website forms is handled in accordance with our Privacy Policy.' },
      ],
    },
    {
      heading: '6. Supplier Applications and Research',
      blocks: [
        { p: 'Suppliers may apply to be considered for the KOR38 Supplier Network.' },
        { p: 'Submitting an application does not guarantee acceptance, referrals, bookings or business opportunities.' },
        {
          p: 'KOR38 may review applications and decide whether a supplier is suitable for potential opportunities.',
        },
        { p: 'The Supplier Insights Survey is provided for research and business development purposes.' },
        {
          p: 'Completing the survey does not constitute an application to join the supplier network, and it does not guarantee future contact or engagement.',
        },
      ],
    },
    {
      heading: '7. Free Resources and Downloads',
      blocks: [
        {
          p: 'KOR38 may provide free educational resources, checklists, worksheets or other downloadable materials.',
        },
        {
          p: 'These resources are intended to offer general guidance and help customers make informed decisions. They do not replace independent checks, professional advice or direct discussions with suppliers.',
        },
        {
          p: 'You may download and use these materials for your own personal or internal business purposes.',
        },
        {
          p: 'Unless we give written permission, you must not sell, republish, redistribute commercially or present KOR38 resources as your own.',
        },
      ],
    },
    {
      heading: '8. Intellectual Property',
      blocks: [
        {
          p: 'Unless otherwise stated, the KOR38 name, logo, branding, website design, original written content, graphics and downloadable resources are owned by KOR38 or used with appropriate permission.',
        },
        {
          p: 'You may view and use website content for personal information and legitimate enquiries about our services.',
        },
        {
          p: 'You must not reproduce, modify, distribute or commercially exploit protected website content without prior written permission, except where permitted by law.',
        },
        {
          p: 'Any third-party trademarks, images or other materials remain the property of their respective owners.',
        },
      ],
    },
    {
      heading: '9. External Websites and Links',
      blocks: [
        {
          p: 'This website may contain links to third-party websites, including independent event suppliers, social media platforms and external resources.',
        },
        { p: 'These links are provided for convenience and information.' },
        {
          p: 'KOR38 does not control third-party websites and is not responsible for their content, availability, security or privacy practices.',
        },
        {
          p: 'A link to an external website does not necessarily constitute an endorsement of all its content or services.',
        },
        {
          p: 'We recommend reviewing the relevant terms and privacy policies before using third-party websites.',
        },
      ],
    },
    {
      heading: '10. Independent Event Suppliers',
      blocks: [
        { p: 'Any event suppliers introduced or referenced through KOR38 operate independently.' },
        {
          p: 'Customers are responsible for discussing requirements, checking suitability, obtaining quotations and entering into agreements directly with their chosen suppliers.',
        },
        {
          p: 'KOR38 does not become a party to a contract between a customer and an independent supplier merely because an introduction has been made.',
        },
        {
          p: 'Further details about our services and responsibilities are provided in the Customer Terms & Conditions.',
        },
      ],
    },
    {
      heading: '11. Website Availability',
      blocks: [
        {
          p: 'We aim to keep this website accessible and functioning correctly. However, we cannot guarantee uninterrupted or error-free access.',
        },
        {
          p: 'The website may occasionally be unavailable because of maintenance, technical problems, updates or circumstances outside our reasonable control.',
        },
        { p: 'We may modify, suspend or discontinue website features where reasonably necessary.' },
      ],
    },
    {
      heading: '12. Privacy and Cookies',
      blocks: [
        {
          p: 'We respect the privacy of website visitors and take reasonable steps to protect personal information.',
        },
        {
          p: 'Our Privacy Policy explains what information we collect, how it is used, when it may be shared and the rights available to individuals.',
        },
        {
          p: 'The website may use cookies or similar technologies for functionality, security and other purposes.',
        },
        {
          p: 'Where non-essential cookies or technologies are introduced, appropriate information and consent controls will be provided where required by law.',
        },
        { p: 'Please refer to our Privacy Policy for further information.' },
      ],
    },
    {
      heading: '13. Limitation of Liability',
      blocks: [
        {
          p: 'We take reasonable care in operating this website and providing information about our services.',
        },
        { p: 'However, general website information is not a guarantee of any particular outcome.' },
        {
          p: 'To the extent permitted by law, KOR38 is not responsible for losses arising solely from reliance on general website content, temporary website unavailability or third-party websites outside our control.',
        },
        {
          p: 'Nothing in these terms excludes or restricts liability where doing so would be unlawful, including liability for death or personal injury caused by negligence, fraud or fraudulent misrepresentation, or rights that cannot legally be limited.',
        },
        {
          p: 'These Website Terms do not limit any statutory rights you may have when purchasing services from KOR38.',
        },
      ],
    },
    {
      heading: '14. Changes to These Terms',
      blocks: [
        {
          p: 'We may update these Website Terms of Use to reflect changes to the website, our services or applicable legal requirements.',
        },
        { p: 'The latest version will be published on this page with an updated revision date.' },
        {
          p: 'Continued use of the website after changes have been published will be subject to the updated terms, to the extent permitted by law.',
        },
        {
          p: 'Changes to these Website Terms will not automatically alter the terms of an existing paid service contract.',
        },
      ],
    },
    {
      heading: '15. Governing Law',
      blocks: [
        {
          p: 'These Website Terms of Use are governed by the laws of England and Wales, subject to any mandatory legal protections that apply.',
        },
        {
          p: 'Any disputes will be subject to the jurisdiction of the courts of England and Wales, except where applicable law provides otherwise.',
        },
      ],
    },
    {
      heading: '16. Contact Us',
      blocks: [
        { p: 'For questions about this website or these terms, please contact:' },
        { p: 'Daniella Farrell, trading as KOR38' },
        { p: 'London, United Kingdom' },
        { p: 'Email: hello@kor38.com' },
      ],
    },
  ],
};